import {
    MeshBasicMaterial,
    RingGeometry,
    CircleGeometry,
    Mesh,
    Vector3,
    Group,
    Raycaster,
    DoubleSide,
    Color,
} from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import Experience from '../Experience.js';
import EventEmitter from '../Utils/EventEmitter.js';
import Events from '../Utils/Events.js';
import Directions from './Directions.js';
import HighlightStates from '../Utils/HighlightState.js';

export default class LocomotionArrowHelper {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.camera = this.experience.camera.instance;
        this.domElement = this.experience.canvas;
        this.pointer = this.experience.pointer;
        this.debug = this.experience.debug;

        // Event handling
        this.eventEmitter = new EventEmitter();

        // Debug
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('Locomotion Arrows');
        }

        this.raycaster = new Raycaster();
        this.isForwardMovementPossible = true;
        this.isForwardArrowHighlighted = false;
        this.isForwardArrowFocused = false;
        this.isBackwardMovementPossible = false;
        this.isBackwardArrowHighlighted = false;
        this.isBackwardArrowFocused = false;
        this.forwardArrowState = HighlightStates.ENABLED;
        this.backwardArrowState = HighlightStates.ENABLED;
        this.shouldSwapArrows = false;

        // Colors
        this.circleEnabledColor = 0xcc4a00;
        this.circleHighlightedColor = 0xdb9c84;
        this.circleFocusedColor = 0x271811;
        this.arrowEnabledColor = 0xffffff;
        this.arrowHighlightedColor = 0xe8e8e8;
        this.arrowFocusedColor = 0xc6c6c6;

        this.offsetFromCamera = new Vector3(0, -0.4, -1.6);

        this.setupMesh();
        this.setupListeners();
        this.repositionArrows();
    }

    setupMesh() {
        this.forwardArrowHelper = this.createArrow();
        this.forwardArrowHelper.name = 'forwardArrowHelper';
        // this.forwardArrowHelper.translate(-0.25, 0, 0);
        // this.forwardArrowHelper.rotateY(Math.PI);
        this.forwardArrowHelper.rotateX(Math.PI / 2);
        this.forwardArrowHelper.position.set(0, 0, -0.45);
        this.forwardArrowHelper.scale.set(1.3, 1.3, 1.3);

        this.backwardArrowHelper = this.createArrow();
        this.backwardArrowHelper.name = 'backwardArrowHelper';
        // this.backwardArrowHelper.geometry.translate(-0.25, 0, 0);
        this.backwardArrowHelper.rotateX(-Math.PI / 2);
        this.backwardArrowHelper.position.set(0, 0, 0.45);
        // this.backwardArrowHelper.visible = true;

        this.arrowHelper = new Group();
        this.arrowHelper.add(this.forwardArrowHelper, this.backwardArrowHelper);
        this.arrowHelper.position.set(
            this.camera.position.x + this.offsetFromCamera.x,
            this.camera.position.y + this.offsetFromCamera.y,
            this.camera.position.z + this.offsetFromCamera.z
        );

        // if (this.debug.active) {
        //     this.debugFolder
        //         .addColor(this.forwardArrowHelper.material, 'color')
        //         .name('forward arrow color');
        //     this.debugFolder
        //         .addColor(this.backwardArrowHelper.material, 'color')
        //         .name('backward arrow color');
        //     this.debugFolder
        //         .add(this, 'arrowHighlightedOpacity')
        //         .name('arrow highlighted opacity')
        //         .min(0.0)
        //         .max(1.0)
        //         .step(0.01);
        //     this.debugFolder
        //         .add(this, 'arrowEnabledOpacity')
        //         .name('arrow enabled opacity')
        //         .min(0.0)
        //         .max(1.0)
        //         .step(0.01);
        // }
        this.scene.add(this.arrowHelper);
    }

    createArrow() {
        // const arrowHelperMaterial = new MeshBasicMaterial({
        //     color: 0xffffff,
        //     transparent: true,
        //     opacity: this.arrowEnabledOpacity,
        //     depthTest: false,
        // });

        // const arrowHelperShape = new Shape();
        // arrowHelperShape.moveTo(0, 0.75);
        // arrowHelperShape.lineTo(0.25, 0);
        // arrowHelperShape.lineTo(0.5, 0.75);
        // arrowHelperShape.lineTo(0.25, 0.55);
        // arrowHelperShape.lineTo(0, 0.75);

        // const arrowHelperGeometry = new ShapeGeometry(arrowHelperShape);

        // const arrowHelper = new Mesh(arrowHelperGeometry, arrowHelperMaterial);
        // return arrowHelper;

        const ringMaterial = new MeshBasicMaterial({
            color: this.arrowEnabledColor,
            side: DoubleSide,
        });
        const ringGeometry = new RingGeometry(0.21, 0.22, 64);
        const ring = new Mesh(ringGeometry, ringMaterial);
        ring.name = 'ring';

        const circleMaterial = new MeshBasicMaterial({
            color: new Color(this.circleEnabledColor),
            side: DoubleSide,
        });
        const circleGeometry = new CircleGeometry(0.175, 64);
        const circle = new Mesh(circleGeometry, circleMaterial);
        circle.name = 'circle';

        const lineMaterial = new LineMaterial({
            color: this.arrowEnabledColor,
            linewidth: 0.004,
            depthTest: false,
            dashed: false,
        });
        const linePoints = [
            0, // point1.x
            0.1, // point1.y
            0, // point1.z
            0, // point2.x
            -0.1, // point2.y
            0, // point2.z
            -0.05, // point3.x
            -0.025, // point3.y
            0, // point3.z
            0, // point4.x
            -0.1, // point4.y
            0, // point4.z
            0.05, // point5.x
            -0.025, // point5.y
            0, // point5.z
        ];
        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(linePoints);
        const line = new Line2(lineGeometry, lineMaterial);
        line.name = 'line';

        const arrowHelper = new Group();
        arrowHelper.add(circle);
        arrowHelper.add(ring);
        arrowHelper.add(line);

        return arrowHelper;
    }

    update() {
        if (this.isForwardMovementPossible) {
            this.forwardArrowHelper.visible = true;
        } else {
            this.forwardArrowHelper.visible = false;
        }

        if (this.isBackwardMovementPossible) {
            this.backwardArrowHelper.visible = true;
        } else {
            this.backwardArrowHelper.visible = false;
        }
    }

    repositionArrows() {
        const localOffset = new Vector3();
        localOffset.copy(this.offsetFromCamera).applyQuaternion(this.camera.quaternion);
        this.arrowHelper.position.copy(this.camera.position).add(localOffset);
        this.arrowHelper.quaternion.copy(this.camera.quaternion);

        if (this.shouldSwapArrows) {
            this.arrowHelper.rotateY(Math.PI);
            this.shouldSwapArrows = false;
        }
    }

    rescaleForwardArrow() {
        this.forwardArrowHelper.scale.set(1.3, 1.3, 1.3);
        this.backwardArrowHelper.scale.set(1, 1, 1);
    }

    rescaleBackwardArrow() {
        this.forwardArrowHelper.scale.set(1, 1, 1);
        this.backwardArrowHelper.scale.set(1.3, 1.3, 1.3);
    }

    setupListeners() {
        this.onArrowHover = (event) => {
            event.preventDefault();
            this.raycaster.setFromCamera(this.pointer.coordinates, this.camera);
            const intersects = this.raycaster.intersectObjects(
                this.arrowHelper.children,
                true
            );

            if (intersects.length > 0) {
                if (
                    intersects[0].object.parent.name === this.forwardArrowHelper.name &&
                    this.isForwardMovementPossible &&
                    this.forwardArrowState == HighlightStates.ENABLED
                ) {
                    this.forwardArrowState = HighlightStates.HIGHLIGHTED;
                    this.updateState(
                        this.forwardArrowHelper,
                        HighlightStates.HIGHLIGHTED
                    );
                } else if (
                    intersects[0].object.parent.name === this.backwardArrowHelper.name &&
                    this.isBackwardMovementPossible &&
                    this.backwardArrowState == HighlightStates.ENABLED
                ) {
                    this.backwardArrowState = HighlightStates.HIGHLIGHTED;
                    this.updateState(
                        this.backwardArrowHelper,
                        HighlightStates.HIGHLIGHTED
                    );
                }
            } else {
                if (
                    this.forwardArrowState == HighlightStates.HIGHLIGHTED ||
                    this.forwardArrowState == HighlightStates.FOCUSED
                ) {
                    this.forwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.forwardArrowHelper, HighlightStates.ENABLED);
                } else if (
                    this.backwardArrowState == HighlightStates.HIGHLIGHTED ||
                    this.backwardArrowState == HighlightStates.FOCUSED
                ) {
                    this.backwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.backwardArrowHelper, HighlightStates.ENABLED);
                }
            }
        };

        this.onArrowFocused = (event) => {
            event.preventDefault();
            this.raycaster.setFromCamera(this.pointer.coordinates, this.camera);
            const intersects = this.raycaster.intersectObjects(
                this.arrowHelper.children,
                true
            );

            if (intersects.length > 0) {
                if (
                    intersects[0].object.parent.name === this.forwardArrowHelper.name &&
                    this.isForwardMovementPossible
                ) {
                    this.forwardArrowState = HighlightStates.FOCUSED;
                    this.updateState(this.forwardArrowHelper, HighlightStates.FOCUSED);
                } else if (
                    intersects[0].object.parent.name === this.backwardArrowHelper.name &&
                    this.isBackwardMovementPossible
                ) {
                    this.backwardArrowState = HighlightStates.FOCUSED;
                    this.updateState(this.backwardArrowHelper, HighlightStates.FOCUSED);
                }
            } else {
                if (this.forwardArrowState == HighlightStates.FOCUSED) {
                    this.forwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.forwardArrowHelper, HighlightStates.ENABLED);
                } else if (this.backwardArrowState == HighlightStates.FOCUSED) {
                    this.backwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.backwardArrowHelper, HighlightStates.ENABLED);
                }
            }
        };

        this.onArrowSelected = (event) => {
            if (!this.arrowHelper.visible) return;

            event.preventDefault();
            this.forwardArrowState = HighlightStates.ENABLED;
            this.backwardArrowState = HighlightStates.ENABLED;

            // Check if an arrow is selected. If so, trigger the event for movement.
            this.raycaster.setFromCamera(this.pointer.coordinates, this.camera);
            const intersects = this.raycaster.intersectObjects(
                this.arrowHelper.children,
                true
            );

            if (intersects.length > 0) {
                if (
                    intersects[0].object.parent.name === this.forwardArrowHelper.name &&
                    this.isForwardMovementPossible
                ) {
                    this.eventEmitter.trigger(Events.TRIGGER_MOVEMENT, [
                        Directions.WAYPOINT_FORWARD,
                    ]);
                    this.forwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.forwardArrowHelper, HighlightStates.ENABLED);
                } else if (
                    intersects[0].object.parent.name === this.backwardArrowHelper.name &&
                    this.isBackwardMovementPossible
                ) {
                    this.eventEmitter.trigger(Events.TRIGGER_MOVEMENT, [
                        Directions.WAYPOINT_BACKWARD,
                    ]);
                    this.backwardArrowState = HighlightStates.ENABLED;
                    this.updateState(this.backwardArrowHelper, HighlightStates.ENABLED);
                }
            }
        };

        this.domElement.addEventListener('mousemove', this.onArrowHover, false);
        this.domElement.addEventListener('mousedown', this.onArrowFocused, false);
        this.domElement.addEventListener('mouseup', this.onArrowSelected, false);
        this.domElement.addEventListener('touchmove', this.onArrowHover, false);
        this.domElement.addEventListener('touchstart', this.onArrowFocused, false);
        this.domElement.addEventListener('touchend', this.onArrowSelected, false);
    }

    destroy() {
        this.domElement.removeEventListener('mousemove', this.onArrowHover, false);
        this.domElement.removeEventListener('mousedown', this.onArrowFocused, false);
        this.domElement.removeEventListener('mouseup', this.onArrowSelected, false);
        this.domElement.removeEventListener('touchmove', this.onArrowHover, false);
        this.domElement.removeEventListener('touchstart', this.onArrowFocused, false);
        this.domElement.removeEventListener('touchend', this.onArrowSelected, false);
    }

    updateState(group, highlightState) {
        group.traverse((child) => {
            if (child instanceof Mesh) {
                if (child.name == 'ring' || child.name == 'line') {
                    switch (highlightState) {
                        case HighlightStates.ENABLED:
                            child.material.color = new Color(this.arrowEnabledColor);
                            break;
                        case HighlightStates.HIGHLIGHTED:
                            child.material.color = new Color(this.arrowHighlightedColor);
                            break;
                        case HighlightStates.FOCUSED:
                            child.material.color = new Color(this.arrowFocusedColor);
                            break;
                        default:
                            child.material.color = new Color(this.arrowEnabledColor);
                            break;
                    }
                } else if (child.name == 'circle') {
                    switch (highlightState) {
                        case HighlightStates.ENABLED:
                            child.material.color = new Color(this.circleEnabledColor);
                            break;
                        case HighlightStates.HIGHLIGHTED:
                            child.material.color = new Color(this.circleHighlightedColor);
                            break;
                        case HighlightStates.FOCUSED:
                            child.material.color = new Color(this.circleFocusedColor);
                            break;
                        default:
                            child.material.color = new Color(this.circleEnabledColor);
                            break;
                    }
                }
            }
        });
    }
}
